import { faLinkSlash } from '@fortawesome/pro-solid-svg-icons'
import clsx from 'clsx'
import type { ReactElement } from 'react'
import { getApiError } from '~/api/problem'
import { Paper } from '~/components/Global/Blocks/Paper/Paper'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { NoResult } from '~/components/Global/Elements/NoResult/NoResult'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import { themeClass } from '~/styles/theme.css'
import useDocumentTitle from '~/utils/documentTitle'
import { usePublicShortlist } from '../queries/publicShortlist'
import {
  card,
  container,
  eyebrow,
  header,
  headerCard,
  headerMain,
  logo,
  sender,
  senderEmail,
  senderLabel,
  senderName,
  stepNumber,
  steps,
  subtitle,
  title
} from './PublicShortlist.css'
import { ShortlistRanking } from './ShortlistRanking'
import { ShortlistThanks } from './ShortlistThanks'

type PublicShortlistPageProps = {
  instance: string
  token: string
}

export const PublicShortlistPage = ({
  instance,
  token
}: PublicShortlistPageProps): ReactElement => {
  useDocumentTitle('Sélection de profils')
  const {
    data: shortlist,
    isLoading,
    isError,
    error
  } = usePublicShortlist(instance, token)
  const isLinkInvalid = getApiError(error)?.status === 404

  return (
    <div className={clsx(themeClass, container)}>
      <div className={card}>
        {isLoading && (
          <Paper>
            <Loader small forceText />
          </Paper>
        )}

        {isLinkInvalid && (
          <Paper>
            <NoResult
              isError
              icon={faLinkSlash}
              message="Ce lien n'est plus valide."
              description="Il a peut-être expiré. Contactez votre interlocuteur pour en recevoir un nouveau."
            />
          </Paper>
        )}

        {isError && !isLinkInvalid && (
          <Paper>
            <NoResult
              isError
              message="Une erreur est survenue."
              description="Veuillez réessayer dans quelques instants."
            />
          </Paper>
        )}

        {shortlist?.submitted && (
          <Paper>
            <ShortlistThanks />
          </Paper>
        )}

        {shortlist && !shortlist.submitted && (
          <>
            <Paper className={headerCard}>
              <header className={header}>
                <div className={headerMain}>
                  {shortlist.logoUrl && (
                    <img
                      src={shortlist.logoUrl}
                      alt={shortlist.customerName ?? ''}
                      className={logo}
                    />
                  )}
                  <div>
                    <div className={eyebrow}>Sélection de profils</div>
                    <h1 className={title}>
                      {shortlist.orderTitle || 'Sélection de profils'}
                    </h1>
                    <p className={subtitle}>
                      {shortlist.applicants.length > 1
                        ? `${shortlist.applicants.length} profils à évaluer`
                        : '1 profil à évaluer'}
                    </p>
                  </div>
                </div>
                {shortlist.senderName && (
                  <div className={sender}>
                    <div>
                      <div className={senderLabel}>Votre interlocuteur</div>
                      <div className={senderName}>{shortlist.senderName}</div>
                      {shortlist.senderEmail && (
                        <a
                          className={senderEmail}
                          href={`mailto:${shortlist.senderEmail}`}
                        >
                          {shortlist.senderEmail}
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </header>
            </Paper>
            <Alert variation={AlertVariation.Info} onDismiss={false}>
              <ol className={steps}>
                <li>
                  <span className={stepNumber}>1</span>
                  Cliquez sur un profil pour afficher son CV
                </li>
                <li>
                  <span className={stepNumber}>2</span>
                  Classez les CV en les faisant glisser ou avec les flèches
                </li>
                <li>
                  <span className={stepNumber}>3</span>
                  Indiquez si vous retenez chaque profil, commentaire facultatif
                </li>
              </ol>
            </Alert>
            <ShortlistRanking
              applicants={shortlist.applicants}
              instance={instance}
              token={token}
            />
          </>
        )}
      </div>
    </div>
  )
}
